// Strategy constants — the UI states, copy and thresholds shared by the strategy page, the report
// list's strategy column and the live-status listener. utils/normalizeStrategy.js maps the report
// strategy endpoint's run statuses onto STRATEGY_STATUS; nothing else reads the API's own values.

// UI state of a report's strategy. GENERATING is the first build (trigger `initial`, week 0);
// UPDATING is a weekly refresh of a strategy that already has a ready week (trigger `refresh`).
// NOT_GENERATED is a report whose strategy has never been started (the endpoint's `run` is null).
export const STRATEGY_STATUS = {
  GENERATING: 'generating',
  UPDATING: 'updating',
  READY: 'ready',
  FAILED: 'failed',
  NOT_GENERATED: 'not_generated',
};

/** A run is going: the live-status poller runs and the page shows progress. */
export const isStrategyInFlight = (status) =>
  status === STRATEGY_STATUS.GENERATING || status === STRATEGY_STATUS.UPDATING;

/** Campaigns start from a strategy with a readable week (a refresh keeps the previous one). */
export const canStartCampaign = (status) =>
  status === STRATEGY_STATUS.READY || status === STRATEGY_STATUS.UPDATING;

// The page title's verb phrase, keyed by the report's objective.
const OBJECTIVE_TITLE = {
  awareness: 'Raising awareness',
  engagement: 'Driving engagement',
  conversion: 'Driving conversions',
};

/** ('awareness', 'Lululemon') → "Raising awareness for Lululemon"; unknown objective → "Strategy for Lululemon". */
export const strategyTitleOf = (objective, productName) => {
  const product = productName || 'your product';
  const lead = OBJECTIVE_TITLE[String(objective || '').toLowerCase()];
  return lead ? `${lead} for ${product}` : `Strategy for ${product}`;
};

// Momentum word thresholds (Figma 477:29083): surging ≥ 50, rising 10–49, flat −9..9, falling ≤ −10.
export const MOMENTUM_WORD = {
  SURGING: 'surging',
  RISING: 'rising',
  FLAT: 'flat',
  FALLING: 'falling',
};

/** A trend's momentum number → its word, or null when there's no number. */
export const momentumWordOf = (value) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  if (number >= 50) return MOMENTUM_WORD.SURGING;
  if (number >= 10) return MOMENTUM_WORD.RISING;
  if (number > -10) return MOMENTUM_WORD.FLAT;
  return MOMENTUM_WORD.FALLING;
};

// The endpoint's momentum direction → its word. It has more steps than the four words: 'fading'
// (STRATEGY_API.md) and 'collapsing' both read as falling.
const MOMENTUM_WORD_OF_DIRECTION = {
  surging: MOMENTUM_WORD.SURGING,
  rising: MOMENTUM_WORD.RISING,
  flat: MOMENTUM_WORD.FLAT,
  fading: MOMENTUM_WORD.FALLING,
  falling: MOMENTUM_WORD.FALLING,
  collapsing: MOMENTUM_WORD.FALLING,
};

/** The ML's `momentum.direction` ('surging' … 'collapsing') → a MOMENTUM_WORD, or null when unknown. */
export const momentumWordOfDirection = (direction) =>
  MOMENTUM_WORD_OF_DIRECTION[
    String(direction ?? '')
      .trim()
      .toLowerCase()
  ] || null;

// The momentum sparkline plots the last SPARKLINE_POINTS months of the ML's monthly series.
export const SPARKLINE_POINTS = 12;

/** An opportunity / priority score → its colour tier: '90' (90–100), '80', '70', or 'low' (< 70 or none). */
export const scoreTierOf = (score) => {
  if (score == null || score === '') return 'low';
  const number = Number(score);
  if (!Number.isFinite(number)) return 'low';
  if (number >= 90) return '90';
  if (number >= 80) return '80';
  if (number >= 70) return '70';
  return 'low';
};

// Tooltip of the disabled "Create campaign" button while the strategy isn't ready.
export const CREATE_CAMPAIGN_LOCKED_TEXT =
  'You can only start campaign creation after the Strategy is fully generated.';

// Tooltip / note of the Strategy entry points (sidebar row, header CTA, Create strategy) while a
// requested intelligence layer isn't ready: a strategy is built from every layer.
export const STRATEGY_LOCKED_TEXT = 'Strategy is available once every intelligence layer is ready.';

// The endpoint's `pendingInputs` keys → the layer names listed under a locked "Create strategy"
// ("Still running: Audience, Cultural"). An unknown key shows as sent.
export const PENDING_INPUT_LABEL = {
  audienceReport: 'Audience',
  culturalReport: 'Cultural',
  competitiveReport: 'Competitive',
};

export const MOMENTUM_TOOLTIP =
  'Momentum: How fast interest in this trend has changed over the last few weeks. Positive means growing, around 0 means steady, and negative means it’s slowing down.';

// The Opportunity score tooltip (Figma 455:129909): an intro line and the five factors, rendered as
// a list by report/StrategyTrends.jsx.
export const OPPORTUNITY_TOOLTIP = {
  intro:
    'Opportunity score measures how promising this trend is for your brand, based on five factors:',
  factors: [
    { label: 'Relevance', text: 'How closely the trend fits your brand.' },
    { label: 'Demand', text: 'How much interest people are showing in it.' },
    { label: 'Reach', text: 'How widely it’s spreading across locations.' },
    {
      label: 'Competition',
      text: 'How many other brands are already in this space. Fewer is better.',
    },
    {
      label: 'Saturation',
      text: 'How close the trend is to its 2-year peak. Lower means more room to grow.',
    },
  ],
};
